import { useRouter } from 'vue-router'
import { useUi } from '@/stores/ui.js'
import { t } from '@/i18n/index.js'
import { useMeta } from '@/stores/meta.js'
import { fieldLabel } from '@/lib/labels.js'

/**
 * One way to report the outcome of a write (saveRecord / recordAction / deleteRecord):
 * saved, queued offline, conflict, invalid, forbidden or error. Returns the result so the
 * caller can show field errors.
 */
export function useSave() {
  const ui = useUi()
  const router = useRouter()

  function report(result, { success = t('messages.saved'), quietSuccess = false, entity = null } = {}) {
    switch (result.status) {
      // The person cancelled filling in what the write needed (D-343): nothing to say.
      case 'cancelled':
        break
      case 'saved':
        if (!quietSuccess) ui.success(success)
        break
      case 'queued':
        ui.warn(t('messages.queued'))
        break
      case 'conflict':
        ui.toast(t('messages.conflict'), { kind: 'warning', timeout: 10000, action: { label: t('offline.resolve'), run: () => router.push({ name: 'sync' }) } })
        break
      case 'invalid': {
        const code = result.data?.code
        ui.error(code === 'FIELD_RULES_UNMET' ? missingMessage(result.errors, entity) : result.message || t('messages.invalid'))
        break
      }
      case 'forbidden':
        ui.error(result.message || t('messages.forbidden'))
        break
      default:
        ui.error(t('messages.error', { message: result.message || '?' }))
    }
    return result
  }

  /** Errors keyed by field for the form ("custom_fields.region" stays as is). */
  function fieldErrors(result) {
    return result?.status === 'invalid' ? result.errors || {} : {}
  }

  return { report, fieldErrors }
}

/**
 * What a write refused for fields the rules require says (D-343): which fields, by their labels
 * ("record.field" keys of a write that makes several records, as qualifying, by the field).
 */
function missingMessage(errors, entity) {
  const keys = Object.keys(errors || {})
  if (!keys.length) return t('messages.rulesUnmet')
  const label = (key) => {
    const [first, ...rest] = key.split('.')
    const [ent, name] = entity ? [entity, key] : useMeta().fields(first)?.length ? [first, rest.join('.')] : [null, key]
    if (!ent) return key
    return fieldLabel(ent, name.startsWith('custom_fields.') ? `cf.${name.slice(14)}` : name.replace(/_uuid$/, ''))
  }
  return t('missingFields.list', { fields: [...new Set(keys.map(label))].join(', ') })
}

/** Handles thrown API errors (online-only calls) with a toast; returns the error. */
export function reportError(error, { entity = null } = {}) {
  const ui = useUi()
  if (error?.name === 'NetworkError' || error?.name === 'TimeoutError') ui.warn(t('messages.onlineOnly'))
  else if (error?.status === 403) ui.error(error.message || t('messages.forbidden'))
  else if (error?.status === 422 && error?.code === 'FIELD_RULES_UNMET') ui.error(missingMessage(error.errors, entity))
  else if (error?.status === 422) ui.error(error.message || t('messages.invalid'))
  else ui.error(t('messages.error', { message: error?.message || '?' }))
  return error
}
